import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Star, Check, X, Trash2, RotateCcw } from "lucide-react";
import { API } from "@/lib/site";

const FILTERS = [
  { value: "pending", label: "Awaiting approval" },
  { value: "approved", label: "Live on site" },
  { value: "rejected", label: "Rejected" },
];

const STATUS_STYLE = {
  pending: "bg-amber-500/15 text-amber-300",
  approved: "bg-[#2E8B57]/20 text-emerald-300",
  rejected: "bg-red-500/15 text-red-300",
};

export const AdminReviews = ({ authHeaders }) => {
  const [reviews, setReviews] = useState(null);
  const [filter, setFilter] = useState("pending");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/reviews`, authHeaders());
      setReviews(data);
    } catch {
      toast.error("Could not load reviews.");
    }
  }, [authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (r, status) => {
    try {
      const { data } = await axios.patch(`${API}/admin/reviews/${r.id}`, { status }, authHeaders());
      setReviews((prev) => prev.map((x) => (x.id === r.id ? data : x)));
      toast.success(status === "approved" ? "Review is now live on the website." : status === "rejected" ? "Review rejected." : "Moved back to pending.");
    } catch {
      toast.error("Could not update the review.");
    }
  };

  const remove = async (r) => {
    try {
      await axios.delete(`${API}/admin/reviews/${r.id}`, authHeaders());
      setReviews((prev) => prev.filter((x) => x.id !== r.id));
      toast.success("Review deleted.");
    } catch {
      toast.error("Could not delete the review.");
    }
  };

  const counts = (reviews || []).reduce((acc, r) => ({ ...acc, [r.status]: (acc[r.status] || 0) + 1 }), {});
  const visible = (reviews || []).filter((r) => r.status === filter);
  const btn = "flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors";

  return (
    <>
      <div className="mt-8">
        <h1 className="font-heading text-3xl font-semibold text-[#F8FFF8] sm:text-4xl">Visitor Reviews</h1>
        <p data-testid="admin-reviews-pending-count" className="mt-1 text-sm text-slate-400">
          {reviews ? `${counts.pending || 0} awaiting approval · ${counts.approved || 0} live` : "Loading…"}
        </p>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            data-testid={`admin-reviews-filter-${f.value}`}
            onClick={() => setFilter(f.value)}
            className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
              filter === f.value ? "bg-white/15 text-white" : "border border-white/10 text-slate-400 hover:text-emerald-300"
            }`}
          >
            {f.label} ({counts[f.value] || 0})
          </button>
        ))}
      </div>

      <div data-testid="admin-reviews-list" className="mt-6 space-y-4">
        {reviews === null ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-[#2E8B57]" />
          </div>
        ) : visible.length === 0 ? (
          <p data-testid="admin-reviews-empty-state" className="rounded-3xl border border-dashed border-white/15 py-20 text-center text-sm text-slate-400">
            {filter === "pending" ? "No reviews waiting. New submissions from the website will appear here." : `No ${filter} reviews.`}
          </p>
        ) : (
          visible.map((r) => (
            <div key={r.id} data-testid={`admin-review-${r.id}`} className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-4">
                  {r.has_photo && (
                    <a href={`${API}/reviews/photo/${r.id}`} target="_blank" rel="noopener noreferrer" data-testid={`admin-review-photo-${r.id}`}>
                      <img src={`${API}/reviews/photo/${r.id}`} alt={`From ${r.name}`} className="h-16 w-16 rounded-xl object-cover" />
                    </a>
                  )}
                  <div>
                    <p className="text-base font-semibold text-[#F8FFF8]">{r.name}</p>
                    <div className="mt-1 flex gap-0.5" aria-label={`${r.rating} stars`}>
                      {Array.from({ length: 5 }).map((_, s) => (
                        <Star key={s} className={`h-3.5 w-3.5 ${s < r.rating ? "fill-[#D97706] stroke-[#D97706]" : "stroke-slate-600"}`} />
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[r.status]}`}>{r.status}</span>
                  {r.status !== "approved" && (
                    <button data-testid={`admin-review-approve-${r.id}`} onClick={() => setStatus(r, "approved")} className={`${btn} bg-[#2E8B57]/20 text-emerald-300 hover:bg-[#2E8B57]/40`}>
                      <Check className="h-3.5 w-3.5" /> Approve
                    </button>
                  )}
                  {r.status === "pending" && (
                    <button data-testid={`admin-review-reject-${r.id}`} onClick={() => setStatus(r, "rejected")} className={`${btn} bg-white/10 text-slate-300 hover:bg-red-500/15 hover:text-red-300`}>
                      <X className="h-3.5 w-3.5" /> Reject
                    </button>
                  )}
                  {r.status !== "pending" && (
                    <button data-testid={`admin-review-unpublish-${r.id}`} onClick={() => setStatus(r, "pending")} className={`${btn} bg-white/10 text-slate-300 hover:text-amber-300`}>
                      <RotateCcw className="h-3.5 w-3.5" /> {r.status === "approved" ? "Unpublish" : "Restore"}
                    </button>
                  )}
                  <button data-testid={`admin-review-delete-${r.id}`} onClick={() => remove(r)} className="rounded-full p-2 text-slate-500 transition-colors hover:bg-red-500/10 hover:text-red-300" aria-label="Delete review">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-slate-300">{r.text}</p>
              <p className="mt-3 text-xs text-slate-500">
                {new Date(r.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
              </p>
            </div>
          ))
        )}
      </div>
    </>
  );
};
